import { useState } from "react";
import { isConfigured, type Settings } from "./store";

/**
 * First-run configuration, and the only place the passphrase is ever typed.
 *
 * Deliberately not a capability URL: putting the read token in a query string
 * would leak it into browser history, the address bar, and any pasted link. One
 * form per device is a small cost for keeping both secrets out of every one of
 * those places.
 */
export default function SettingsForm({
  initial,
  error,
  onSave,
  onCancel,
}: {
  initial: Settings;
  error?: string;
  onSave: (s: Settings) => void;
  onCancel?: () => void;
}) {
  const [form, setForm] = useState<Settings>(initial);
  const ready = isConfigured(form);

  return (
    <main className="mx-auto w-full max-w-lg flex-1 px-4 py-12 sm:px-6">
      {/* Matches the dashboard header. This screen is the first thing an
          unconfigured device shows, so it is where the mark most needs to be. */}
      <div className="flex items-center gap-3">
        <img
          src="./favicon.svg"
          alt=""
          width={36}
          height={36}
          className="size-9 shrink-0"
        />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">StatsAI</h1>
          <p className="mt-0.5 text-sm text-muted">
            Connect this device to your dead drop.
          </p>
        </div>
      </div>

      {error && (
        <p className="mt-5 rounded-md border border-critical/30 bg-critical/10 px-3 py-2 text-xs text-critical">
          {error}
        </p>
      )}

      <form
        className="mt-6 space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) onSave(form);
        }}
      >
        <Field
          label="Dead-drop URL"
          hint="The Worker you deployed, e.g. https://statsai-deaddrop.<you>.workers.dev"
          value={form.dropUrl}
          onChange={(dropUrl) => setForm({ ...form, dropUrl })}
          type="url"
          placeholder="https://statsai-deaddrop.example.workers.dev"
        />
        <Field
          label="Read token"
          hint="The READ_TOKEN secret. Grants read access only — it cannot publish."
          value={form.readToken}
          onChange={(readToken) => setForm({ ...form, readToken })}
          type="password"
        />
        <Field
          label="Passphrase"
          hint="Decrypts your data in this browser. Never sent anywhere, including to the drop."
          value={form.passphrase}
          onChange={(passphrase) => setForm({ ...form, passphrase })}
          type="password"
        />

        <div className="flex items-center gap-3 pt-1">
          <button
            type="submit"
            disabled={!ready}
            className="rounded-lg bg-surface-3 px-4 py-2 text-sm font-medium transition-colors hover:bg-border-strong disabled:cursor-not-allowed disabled:opacity-40"
          >
            Connect
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="text-sm text-muted transition-colors hover:text-foreground"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <p className="mt-8 text-[11px] leading-relaxed text-faint">
        All three are stored in this browser only. The drop holds ciphertext it
        has no key for, so nothing here is recoverable from the server — if you
        forget the passphrase, the stored history cannot be read by anyone,
        including you.
      </p>
    </main>
  );
}

function Field({
  label,
  hint,
  value,
  onChange,
  type,
  placeholder,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
  type: string;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        autoComplete="off"
        className="mt-1.5 w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm outline-none transition-colors focus:border-border-strong"
      />
      <span className="mt-1 block text-[11px] text-faint">{hint}</span>
    </label>
  );
}
